import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider } from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";

import TabPanel from "../../../../shared/UI/TabPanel";
import { getEntityProfileRoute } from "../../../../routers/routeUtils";

import { useEntityBranch } from "../../../Entity/api/queries/useEntityBranch";
import { EntityBranchFormData } from "../../../Entity/types/entityBranch.types";
import { entityBranchSchema } from "../../../Entity/schemas/entityBranch.schema";
import { useUpdateEntityBranch } from "../../../Entity/api/mutations/branch.mutation";

import SportsTab from "./SportsTab";
import BasicInfoTab from "./BasicInfoTab";
import FacilitiesTab from "./FacilitiesTab";
import TabsNavigation from "./TabsNavigation";
import WorkingHoursTab from "./WorkingHoursTab";
import { toast } from "react-toastify";

const TABS = [
  {
    value: "basicInfo",
    component: (isLoading: boolean) => <BasicInfoTab isBranchDataLoading={isLoading} />,
  },
  {
    value: "workingHours",
    component: (isLoading: boolean) => <WorkingHoursTab isBranchDataLoading={isLoading} />,
  },
  {
    value: "sports",
    component: (isLoading: boolean) => <SportsTab isBranchDataLoading={isLoading} />,
  },
  {
    value: "facilities",
    component: (isLoading: boolean) => <FacilitiesTab isBranchDataLoading={isLoading} />,
  },
];

const EntityProfileEditBranch = () => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEditBranch" });
  const { entityId, branchId } = useParams<{ entityId: string; branchId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get("tab") || "basicInfo";

  const { data, isLoading, error } = useEntityBranch(branchId!);

  const defaultValues = {
    countryId: data?.country.id,
    name: data?.name,
    address: data?.address,
    googleMapsLink: data?.googleMapsLink,
    isHeadquarter: data?.isHeadquarter,
    facilities: data?.facilities.map((facility) => ({
      id: facility.footballFaciliteItemId,
      quantity: facility.quantity,
    })),
    sports: data?.sports.map((sport) => sport.sportId),
    workingHours: data?.workingHours.map((workingHour) => ({
      dayOfWeek: workingHour.dayOfWeek,
      openingTime: workingHour.openingTime,
      closingTime: workingHour.closingTime,
      isClosed: workingHour.isClosed,
    })),
  };

  const methods = useForm<EntityBranchFormData>({
    resolver: zodResolver(entityBranchSchema),
    mode: "all",
    defaultValues,
  });

  useEffect(() => {
    if (!data) return;
    methods.reset(defaultValues);
  }, [data, methods]);

  const { mutate: updateBranch, isPending: isUpdatingBranch } = useUpdateEntityBranch(branchId!);

  const onSubmit = async (formData: EntityBranchFormData) => {
    updateBranch(formData, {
      onSuccess: () => {
        toast.success(t("branchUpdated"));

        setTimeout(() => {
          navigate(getEntityProfileRoute(entityId!));
        }, 1000);
      },
    });
  };

  const handleCancel = () => {
    methods.reset(defaultValues);
    navigate(getEntityProfileRoute(entityId!));
  };

  return (
    <Box sx={styles.container}>
      <TabsNavigation />
      <FormProvider {...methods}>
        <Box component="form" onSubmit={methods.handleSubmit(onSubmit)} sx={styles.tabsContainer}>
          {TABS.map((tab) => (
            <TabPanel key={tab.value} value={currentTab} tab={tab.value}>
              {tab.component(isLoading)}
            </TabPanel>
          ))}
          <Divider flexItem orientation="horizontal" />
          <Box sx={styles.buttonsContainer}>
            <Button variant="outlined-md" onClick={handleCancel}>
              {t("cancel")}
            </Button>
            <Button variant="primary-md" type="submit" disabled={isUpdatingBranch}>
              {t("save")}
            </Button>
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default EntityProfileEditBranch;

const styles = {
  container: { display: "flex", flexDirection: "column", gap: "16px" },
  tabsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "24px 0",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "0 16px",
  },
};
